import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { PixelRouteTracker } from "@/components/PixelRouteTracker";
import { RouteRobots } from "@/components/seo/RouteRobots";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { PlanPreviewProvider } from "@/contexts/PlanPreviewContext";
import PlanPreviewBanner from "@/components/preview/PlanPreviewBanner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { PWAInstallBanner } from "@/components/PWAInstallBanner";
import { UpdateNotification } from "@/components/UpdateNotification";
import { OfflineIndicator, OnlineIndicator } from "@/components/OfflineIndicator";
import { lazy, Suspense } from "react";

// Critical routes - loaded immediately for better initial render
// (menú público del comensal: es la ruta más sensible al rendimiento)
import PublicMenu from "./pages/PublicMenu";
import PublicMenuBySlug from "./pages/PublicMenuBySlug";

// Non-critical routes - lazy loaded to reduce initial bundle size
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const MenuDigitalGratis = lazy(() => import("./pages/MenuDigitalGratis"));
const MenuDemo = lazy(() => import("./pages/MenuDemo"));
const MenuDigitalQr = lazy(() => import("./pages/solutions/MenuDigitalQr"));
const ConvertirMenuPdf = lazy(() => import("./pages/solutions/ConvertirMenuPdf"));
const CodigoQrMenu = lazy(() => import("./pages/solutions/CodigoQrMenu"));
const ActualizarPreciosMenu = lazy(() => import("./pages/solutions/ActualizarPreciosMenu"));
const MenuDigitalWhatsapp = lazy(() => import("./pages/solutions/MenuDigitalWhatsapp"));
const Precios = lazy(() => import("./pages/solutions/Precios"));
const MenuDigitalTaqueria = lazy(() => import("./pages/solutions/MenuDigitalTaqueria"));
const TrialPreview = lazy(() => import("./pages/TrialPreview"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminClients = lazy(() => import("./pages/AdminClients"));
const AdminManageClient = lazy(() => import("./pages/AdminManageClient"));
const AdminRouteGuard = lazy(() =>
  import("./components/admin/AdminRouteGuard").then((m) => ({ default: m.AdminRouteGuard })),
);
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost1 = lazy(() => import("./pages/BlogPost1"));
const BlogPost2 = lazy(() => import("./pages/BlogPost2"));
const BlogPost3 = lazy(() => import("./pages/BlogPost3"));
const BlogPost4 = lazy(() => import("./pages/BlogPost4"));
const BlogPost5 = lazy(() => import("./pages/BlogPost5"));
const BlogPost6 = lazy(() => import("./pages/BlogPost6"));
const BlogPost7 = lazy(() => import("./pages/BlogPost7"));
const BlogPost8 = lazy(() => import("./pages/BlogPost8"));
const BlogPost9 = lazy(() => import("./pages/BlogPost9"));
const BlogPost10 = lazy(() => import("./pages/BlogPost10"));
const BlogPost11 = lazy(() => import("./pages/BlogPost11"));
const BlogPost12 = lazy(() => import("./pages/BlogPost12"));
const BlogPost13 = lazy(() => import("./pages/BlogPost13"));
const CasosExito = lazy(() => import("./pages/CasosExito"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Comparativa = lazy(() => import("./pages/Comparativa"));
const Support = lazy(() => import("./pages/Support"));
const Favoritos = lazy(() => import("./pages/Favoritos"));
const ReferidosInfo = lazy(() => import("./pages/ReferidosInfo"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// HR Module - lazy loaded
const HRLayout = lazy(() => import("./pages/rh/HRLayout"));
const AccountSuspensionGate = lazy(() => import("./components/AccountSuspensionGate"));
const ModuleAccessGate = lazy(() => import("./components/ModuleAccessGate"));
const HRDashboard = lazy(() => import("./pages/rh/HRDashboard"));
const HROrganizations = lazy(() => import("./pages/rh/HROrganizations"));
const HRBranches = lazy(() => import("./pages/rh/HRBranches"));
const HRDepartments = lazy(() => import("./pages/rh/HRDepartments"));
const HRPositions = lazy(() => import("./pages/rh/HRPositions"));
const HREmployees = lazy(() => import("./pages/rh/HREmployees"));
const HREmployeeDetail = lazy(() => import("./pages/rh/HREmployeeDetail"));
const HRIncidents = lazy(() => import("./pages/rh/HRIncidents"));
const HRSanctions = lazy(() => import("./pages/rh/HRSanctions"));
const HRBonuses = lazy(() => import("./pages/rh/HRBonuses"));
const HRTimeOff = lazy(() => import("./pages/rh/HRTimeOff"));
const HRPayroll = lazy(() => import("./pages/rh/HRPayroll"));
const HRPayrollDetail = lazy(() => import("./pages/rh/HRPayrollDetail"));
const HRIndexRouter = lazy(() => import("./pages/rh/HRIndexRouter"));
const HRNotifications = lazy(() => import("./pages/rh/HRNotifications"));
const HRNotificationPreferences = lazy(() => import("./pages/rh/HRNotificationPreferences"));
const HRQuietHoursPreferences = lazy(() => import("./pages/rh/HRQuietHoursPreferences"));
const MyProfile = lazy(() => import("./pages/rh/employee/MyProfile"));
const MyTimeOff = lazy(() => import("./pages/rh/employee/MyTimeOff"));
const MyIncidents = lazy(() => import("./pages/rh/employee/MyIncidents"));
const MySanctions = lazy(() => import("./pages/rh/employee/MySanctions"));
const MyPayroll = lazy(() => import("./pages/rh/employee/MyPayroll"));
const MyBonuses = lazy(() => import("./pages/rh/employee/MyBonuses"));
const HRReports = lazy(() => import("./pages/rh/HRReports"));
const HRScheduledReports = lazy(() => import("./pages/rh/HRScheduledReports"));
const HRDemoHub = lazy(() => import("./pages/rh/HRDemoHub"));
const HRAttendance = lazy(() => import("./pages/rh/HRAttendance"));
const MyAttendance = lazy(() => import("./pages/rh/employee/MyAttendance"));
const HRAccess = lazy(() => import("./pages/rh/HRAccess"));
const HRMasterCatalog = lazy(() => import("./pages/rh/HRMasterCatalog"));
const HRKiosk = lazy(() => import("./pages/rh/HRKiosk"));
const HRAttendanceMark = lazy(() => import("./pages/rh/HRAttendanceMark"));
const HROnboarding = lazy(() => import("./pages/rh/HROnboarding"));
const SoftwareRH = lazy(() => import("./pages/SoftwareRH"));
const AccesoRapido = lazy(() => import("./pages/AccesoRapido"));
const ManagerWelcome = lazy(() => import("./pages/rh/onboarding/ManagerWelcome"));
const EmployeeWelcome = lazy(() => import("./pages/rh/onboarding/EmployeeWelcome"));

// Optimized QueryClient para funcionalidad offline
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 15, // 15 minutos (más tiempo para offline)
      gcTime: 1000 * 60 * 60, // 1 hora (mantener en memoria más tiempo)
      retry: (failureCount, error) => {
        // No reintentar si estamos offline
        if (!navigator.onLine) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      // Estrategia más agresiva para mantener datos offline
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
  },
});

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <PlanPreviewProvider>
          <BrowserRouter>
            <PixelRouteTracker />
            <RouteRobots />
            <PlanPreviewBanner />
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/landing-ads" element={<LandingPage />} />
                <Route path="/software-recursos-humanos" element={<SoftwareRH />} />
                <Route path="/menu-digital-gratis" element={<MenuDigitalGratis />} />
                <Route path="/menu-demo" element={<MenuDemo />} />
                <Route path="/menu-digital-qr" element={<MenuDigitalQr />} />
                <Route path="/convertir-menu-pdf-a-digital" element={<ConvertirMenuPdf />} />
                <Route path="/codigo-qr-menu-restaurante" element={<CodigoQrMenu />} />
                <Route path="/actualizar-precios-menu" element={<ActualizarPreciosMenu />} />
                <Route path="/menu-digital-whatsapp" element={<MenuDigitalWhatsapp />} />
                <Route path="/precios" element={<Precios />} />
                <Route path="/menu-digital-taqueria" element={<MenuDigitalTaqueria />} />
                <Route path="/prueba-tu-menu" element={<TrialPreview />} />
                <Route path="/privacidad" element={<Privacy />} />
                <Route path="/terminos" element={<Terms />} />
                <Route path="/support" element={<Support />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/dashboard"
                  element={
                    <AccountSuspensionGate>
                      <ModuleAccessGate>
                        <Dashboard />
                      </ModuleAccessGate>
                    </AccountSuspensionGate>
                  }
                />
                <Route
                  path="/bienvenida"
                  element={
                    <AccountSuspensionGate>
                      <Onboarding />
                    </AccountSuspensionGate>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <AdminRouteGuard>
                      <AdminDashboard />
                    </AdminRouteGuard>
                  }
                />
                <Route
                  path="/admin/clientes"
                  element={
                    <AdminRouteGuard>
                      <AdminClients />
                    </AdminRouteGuard>
                  }
                />
                <Route
                  path="/admin/clientes/:restaurantId"
                  element={
                    <AdminRouteGuard>
                      <AdminManageClient />
                    </AdminRouteGuard>
                  }
                />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/menu-digital-qr-restaurante-mexico" element={<BlogPost1 />} />
                <Route path="/blog/restaurante-necesita-menus-digitales" element={<BlogPost2 />} />
                <Route path="/blog/menus-qr-vs-tradicionales-ventas" element={<BlogPost3 />} />
                <Route path="/blog/menu-digital-gratis-vs-profesional" element={<BlogPost4 />} />
                <Route path="/blog/olaclick-vs-plaxter-comparativa-2025" element={<BlogPost5 />} />
                <Route path="/blog/precios-menu-digital-mexico-2025" element={<BlogPost6 />} />
                <Route path="/blog/menu-digital-funciona-sin-internet-pwa" element={<BlogPost7 />} />
                <Route path="/blog/aumentar-ventas-restaurante-menu-digital-caso-real" element={<BlogPost8 />} />
                <Route path="/blog/errores-fatales-menus-digitales-gratuitos" element={<BlogPost9 />} />
                <Route path="/blog/menu-digital-whatsapp-integrado-mexico" element={<BlogPost10 />} />
                <Route path="/blog/calisto-ai-vs-plaxter-menu-digital-ia" element={<BlogPost11 />} />
                <Route path="/blog/menu-digital-taqueria-negocio-pequeno-mexico" element={<BlogPost12 />} />
                <Route path="/blog/por-que-restaurantes-abandonan-menu-digital-regresan-papel" element={<BlogPost13 />} />
                <Route path="/casos-exito-restaurantes-mexico" element={<CasosExito />} />
                <Route path="/preguntas-frecuentes-menus-digitales-mexico" element={<FAQ />} />
                <Route path="/comparativa-menus-digitales-mexico" element={<Comparativa />} />
                <Route path="/favoritos" element={<Favoritos />} />
                <Route path="/referidos-info" element={<ReferidosInfo />} />
                
                {/* Acceso rápido por link/QR */}
                <Route path="/acceso/:token" element={<AccesoRapido />} />

                {/* Kiosco de asistencia y marcaje por QR (pantalla completa) */}
                <Route path="/rh/kiosco" element={<HRKiosk />} />
                <Route path="/rh/marcar" element={<HRAttendanceMark />} />

                {/* HR Module */}
                <Route
                  path="/rh"
                  element={
                    <AccountSuspensionGate>
                      <HRLayout />
                    </AccountSuspensionGate>
                  }
                >
                  <Route path="bienvenida-gerente" element={<ManagerWelcome />} />
                  <Route path="bienvenida-empleado" element={<EmployeeWelcome />} />
                  <Route index element={<HRIndexRouter />} />
                  <Route path="mi-perfil" element={<MyProfile />} />
                  <Route path="mis-solicitudes" element={<MyTimeOff />} />
                  <Route path="mis-incidencias" element={<MyIncidents />} />
                  <Route path="mis-sanciones" element={<MySanctions />} />
                  <Route path="mi-nomina" element={<MyPayroll />} />
                  <Route path="mis-bonos" element={<MyBonuses />} />
                  <Route path="organizaciones" element={<HROrganizations />} />
                  <Route path="sucursales" element={<HRBranches />} />
                  <Route path="departamentos" element={<HRDepartments />} />
                  <Route path="puestos" element={<HRPositions />} />
                  <Route path="empleados" element={<HREmployees />} />
                  <Route path="empleados/:employeeId" element={<HREmployeeDetail />} />
                  <Route path="incidencias" element={<HRIncidents />} />
                  <Route path="sanciones" element={<HRSanctions />} />
                  <Route path="bonos" element={<HRBonuses />} />
                  <Route path="vacaciones" element={<HRTimeOff />} />
                  <Route path="nomina" element={<HRPayroll />} />
                  <Route path="nomina/:periodId" element={<HRPayrollDetail />} />
                  <Route path="reportes" element={<HRReports />} />
                  <Route path="reportes-programados" element={<HRScheduledReports />} />
                  <Route path="demo" element={<HRDemoHub />} />
                  <Route path="asistencia" element={<HRAttendance />} />
                  <Route path="accesos" element={<HRAccess />} />
                  <Route path="catalogo-maestro" element={<HRMasterCatalog />} />
                  <Route path="inicio" element={<HROnboarding />} />
                  <Route path="mi-asistencia" element={<MyAttendance />} />
                  <Route path="notificaciones" element={<HRNotifications />} />
                  <Route path="preferencias-notificaciones" element={<HRNotificationPreferences />} />
                  <Route path="preferencias-silencio" element={<HRQuietHoursPreferences />} />
                </Route>
                <Route path="/r/:slugOrId" element={<PublicMenu />} />
                <Route path="/:slug" element={<PublicMenuBySlug />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            
            {/* Componentes PWA para funcionalidad offline */}
            <PWAInstallPrompt />
            <PWAInstallBanner />
            <UpdateNotification />
            <OfflineIndicator />
            <OnlineIndicator />
          </BrowserRouter>
          </PlanPreviewProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
